import React, { useState, useEffect } from 'react';
import './AddToRoutineBottomSheet.css';
import Typography from '../components/Text/Typography';
import SearchCard from '../components/SearchCard/SearchCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { BottomSheet } from '../components/BottomSheet';
import Icon from '../components/Icon/Icon';
import Button from '../components/Button/Button';
import Banner from '../components/Banner/Banner';
import ButtonGroup from '../components/Button/ButtonGroup';
import { AVAILABLE_ACTIONS } from '../utils/actions';
import { API_BASE_URL, authenticatedFetch } from '../config/api';

interface Product {
  product_id: number;
  product_name: string;
  brand: string;
  category: string;
  image_url?: string;
}

interface Action {
  id: string;
  name: string;
  description: string;
}

interface AddToRoutineBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (productId: number) => void;
  onAddAction?: (actionId: string) => void;
  routineType: 'AM' | 'PM';
  selectedDay: string;
  currentRoutineProducts: any[]; // Mixed array of products and actions
  userId: number;
}

const AddToRoutineBottomSheet: React.FC<AddToRoutineBottomSheetProps> = ({ 
  isOpen, 
  onClose, 
  onAddProduct, 
  onAddAction,
  routineType, 
  selectedDay, 
  currentRoutineProducts,
  userId 
}) => {
  const [cabinetProducts, setCabinetProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [activeTab, setActiveTab] = useState<string>('Products');



  useEffect(() => {
    if (isOpen) {
      // User is guaranteed to be available by AuthGuard
      setLoading(true);
      fetchCabinetProducts();
    }
  }, [isOpen, userId]);

  const fetchCabinetProducts = async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/products/${userId}`);
      if (!response.ok) throw new Error('Failed to fetch cabinet products');
      const data = await response.json();
      setCabinetProducts(data);
    } catch (error) {
      console.error('Error fetching cabinet products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter out products that are already in the current routine. The routine can
  // arrive un-loaded or malformed from the server, so never assume an array.
  const routineItems = Array.isArray(currentRoutineProducts) ? currentRoutineProducts : [];
  const availableProducts = cabinetProducts.filter(cabinetProduct =>
    !routineItems.some(routineItem =>
      routineItem.product_id && routineItem.product_id === cabinetProduct.product_id
    )
  );

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleAddProduct = async () => {
    if (selectedProduct) {
      try {
        // Add the product to the routine via API
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/${userId}/${routineType}/${selectedDay}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            productId: selectedProduct.product_id
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add product to routine');
        }
        
        // Call the parent callback to refresh the routine
        onAddProduct(selectedProduct.product_id);
        setSelectedProduct(null);
        onClose();
      } catch (error) {
        console.error('Error adding product to routine:', error);
        alert(`Failed to add product to routine: ${error.message}`);
      }
    }
  };

  const handleAddAction = () => {
    if (selectedAction && onAddAction) {
      onAddAction(selectedAction.id);
      setSelectedAction(null);
      onClose();
    }
  };

  const handleActionClick = (action: Action) => {
    setSelectedAction(action);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSelectedProduct(null);
    setSelectedAction(null);
  };

  const handleClose = () => {
    setSelectedProduct(null);
    setSelectedAction(null);
    onClose();
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add to ${routineType === 'AM' ? 'Morning' : 'Night'} Routine`}
      height="80vh"
      className="add-to-routine-bottom-sheet"
    >
      <div className="add-to-routine-content">
        <div className="add-to-routine-tabs">
          <ButtonGroup
            options={['Products', 'Actions']}
            value={activeTab}
            onChange={handleTabChange}
          />
        </div>

        {activeTab === 'Products' ? (
          <>
            <Banner variant="info">
              Select a product from your skincare cabinet to add to your {routineType === 'AM' ? 'morning' : 'night'} routine for {selectedDay}.
            </Banner>

            {loading ? (
              <div className="loading-container">
                <LoadingSpinner text="Loading your cabinet..." size="small" />
              </div>
            ) : availableProducts.length === 0 ? (
              <Banner variant="tip">
                All products in your cabinet are already in this routine.
              </Banner>
            ) : (
              <div className="products-list">
                {availableProducts.map((product) => (
                  <SearchCard
                    key={product.product_id}
                    product={product}
                    isSelected={selectedProduct?.product_id === product.product_id}
                    onClick={() => handleProductClick(product)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <Banner variant="info">
              Select an action to add to your {routineType === 'AM' ? 'morning' : 'night'} routine for {selectedDay}.
            </Banner>

            <div className="actions-list">
              {AVAILABLE_ACTIONS.map((action) => (
                <div
                  key={action.id}
                  className={`action-card ${selectedAction?.id === action.id ? 'selected' : ''}`}
                  onClick={() => handleActionClick(action)}
                >
                  <Typography variant="body-bold">{action.name}</Typography>
                  <Typography variant="body-small">{action.description}</Typography>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="add-to-routine-footer">
        <Button variant="secondary" onClick={handleClose} className="cancel-button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={activeTab === 'Products' ? handleAddProduct : handleAddAction}
          disabled={activeTab === 'Products' ? !selectedProduct : !selectedAction}
          className="add-button"
        >
          Add to Routine
        </Button>
      </div>
    </BottomSheet>
  );
};

export default AddToRoutineBottomSheet;
